import React, { useEffect, useState, useMemo } from 'react';
import {
  FiTrendingDown,
  FiTrendingUp,
  FiCalendar,
  FiUsers,
  FiEye,
  FiUserPlus,
  FiActivity,
} from 'react-icons/fi';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import api from '../services/Api';
import Title from '../components/Title';

const MetricCard = ({ title, value, change, isPositive, icon: Icon }) => (
  <div className="flex flex-col p-6 transition-all duration-200 bg-white border border-gray-100 rounded-xl font-nohemi hover:border-gray-200">
    <div className="flex items-start justify-between mb-4">
      <div className="p-2 rounded-lg bg-gray-50">
        <Icon className="w-5 h-5 text-gray-600" />
      </div>
      <div className="flex items-center">
        {isPositive ? (
          <FiTrendingUp className="w-4 h-4 text-green-500" />
        ) : (
          <FiTrendingDown className="w-4 h-4 text-red-500" />
        )}
        <span
          className={`ml-1 text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
          {Math.abs(change).toFixed(1)}%
        </span>
      </div>
    </div>
    <h3 className="mb-2 text-sm font-medium text-gray-500">{title}</h3>
    <p className="text-2xl font-bold text-gray-800">{value.toLocaleString()}</p>
  </div>
);

const ReportAnalytics = () => {
  const [metrics, setMetrics] = useState({
    newUsers: 0,
    activeUsers: 0,
    visitCount: 0,
    uniqueVisitors: 0,
  });
  const [previousMetrics, setPreviousMetrics] = useState({
    newUsers: 0,
    activeUsers: 0,
    visitCount: 0,
    uniqueVisitors: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [weeklyData, setWeeklyData] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current === 0 ? 0 : 100;
    return ((current - previous) / previous) * 100;
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const selectedDateString = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(
    selectedDay
  ).padStart(2, '0')}`;

  const barChartData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return [
        {
          date: selectedDateString || 'No date selected',
          'New Users': 0,
          'Active Users': 0,
          Visits: 0,
          'Unique Visitors': 0,
        },
      ];
    }

    const values = {
      'New Users': 0,
      'Active Users': 0,
      Visits: 0,
      'Unique Visitors': 0,
    };

    chartData.forEach(item => {
      if (item.name in values) {
        values[item.name] = Number(item.value) || 0;
      }
    });

    const finalData = [
      {
        date: selectedDateString,
        ...values,
      },
    ];

    return finalData;
  }, [chartData, selectedDateString]);

  const generateInsights = useMemo(() => {
    const list = [];

    if (metrics.newUsers >= 8) {
      list.push({
        type: 'acquisition_strong',
        message: `Strong day for growth: **${metrics.newUsers}** new users joined today`,
        color: 'green',
        icon: '🎯',
      });
    } else if (metrics.newUsers > 0) {
      list.push({
        type: 'new_users',
        message: `${metrics.newUsers} new user${metrics.newUsers === 1 ? '' : 's'} joined today`,
        color: 'gray',
      });
    }

    if (metrics.visitCount > 0 && metrics.uniqueVisitors > 0) {
      const pagesPerVisitor = (metrics.visitCount / metrics.uniqueVisitors).toFixed(1);
      if (Number(pagesPerVisitor) >= 4.2) {
        list.push({
          type: 'deep_engagement',
          message: `High engagement — visitors viewed **${pagesPerVisitor}** pages on average`,
          color: 'blue',
          icon: '🔥',
        });
      } else if (Number(pagesPerVisitor) <= 1.4 && metrics.visitCount > 40) {
        list.push({
          type: 'bounce_concern',
          message: `Low pages/visit (${pagesPerVisitor}) — possible high bounce rate`,
          color: 'orange',
          icon: '⚠️',
        });
      }
    }

    if (metrics.activeUsers > 0 && metrics.uniqueVisitors > 0) {
      const engagementRate = ((metrics.activeUsers / metrics.uniqueVisitors) * 100).toFixed(0);
      if (Number(engagementRate) >= 60) {
        list.push({
          type: 'high_engagement',
          message: `Very strong session quality — **${engagementRate}%** of visitors were active`,
          color: 'green',
        });
      } else if (Number(engagementRate) <= 25 && metrics.uniqueVisitors > 30) {
        list.push({
          type: 'low_engagement',
          message: `Low activity rate — only **${engagementRate}%** of visitors were active`,
          color: 'red',
        });
      }
    }

    if (weeklyData.length >= 4) {
      const dayTotals = Array(7).fill(0);
      const dayCounts = Array(7).fill(0);
      weeklyData.forEach(day => {
        const dow = new Date(day.date).getDay();
        dayTotals[dow] += day.visitCount || 0;
        dayCounts[dow]++;
      });

      let bestDow = 0;
      let bestAvg = -1;
      dayTotals.forEach((total, i) => {
        const avg = dayCounts[i] > 0 ? total / dayCounts[i] : 0;
        if (avg > bestAvg) {
          bestAvg = avg;
          bestDow = i;
        }
      });

      const dayNames = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      if (bestAvg > 0) {
        list.push({
          type: 'peak_day',
          message: `${dayNames[bestDow]} is your strongest weekday this week (avg traffic)`,
          color: 'purple',
        });
      }
    }

    if (list.length < 2 && weeklyData.length > 0) {
      const totalVisits = weeklyData.reduce((sum, d) => sum + (d.visitCount || 0), 0);
      const avg = Math.round(totalVisits / weeklyData.length);
      list.push({
        type: 'week_avg',
        message: `This week: ~${avg} visits per day`,
        color: 'gray',
      });
    }

    return list.slice(0, 5);
  }, [metrics, weeklyData]);

  useEffect(() => {
    const fetchTodayStats = async () => {
      try {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        const [todayRes, yesterdayRes] = await Promise.all([
          api.get(`/analytics/user-stats?date=${today}`),
          api.get(`/analytics/user-stats?date=${yesterdayStr}`),
        ]);

        if (todayRes.data.success) {
          setMetrics(todayRes.data.data);
          console.log('Today metrics set:', todayRes.data.data);
        }
        if (yesterdayRes.data.success) {
          setPreviousMetrics(yesterdayRes.data.data);
        }
      } catch (err) {
        console.error('Failed to load daily stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTodayStats();
  }, []);

  useEffect(() => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    if (selectedDay > daysInMonth) setSelectedDay(daysInMonth);
  }, [selectedMonth, selectedYear, selectedDay]);

  useEffect(() => {
    const fetchSelectedDate = async () => {
      try {
        const date = new Date(selectedYear, selectedMonth - 1, selectedDay);
        const dateStr = date.toISOString().split('T')[0];

        const res = await api.get(`/analytics/user-stats?date=${dateStr}`);
        if (res.data.success) {
          const d = res.data.data;
          setChartData([
            { name: 'New Users', value: d.newUsers },
            { name: 'Active Users', value: d.activeUsers },
            { name: 'Visits', value: d.visitCount },
            { name: 'Unique Visitors', value: d.uniqueVisitors },
          ]);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchSelectedDate();
  }, [selectedDay, selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchWeekly = async () => {
      try {
        const today = new Date();
        const promises = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          promises.push(api.get(`/analytics/user-stats?date=${d.toISOString().split('T')[0]}`));
        }
        const results = await Promise.all(promises);
        const valid = results.filter(r => r.data?.success).map(r => r.data.data);
        setWeeklyData(valid);
      } catch (err) {
        console.error('Weekly fetch failed', err);
      }
    };

    fetchWeekly();
  }, []);

  useEffect(() => {
    setInsights(generateInsights);
  }, [generateInsights]);

  const metricCards = [
    {
      title: 'Visit Count',
      value: metrics.visitCount,
      change: calculatePercentageChange(metrics.visitCount, previousMetrics.visitCount),
      isPositive: metrics.visitCount >= previousMetrics.visitCount,
      icon: FiEye,
    },
    {
      title: 'Unique Visitors',
      value: metrics.uniqueVisitors,
      change: calculatePercentageChange(metrics.uniqueVisitors, previousMetrics.uniqueVisitors),
      isPositive: metrics.uniqueVisitors >= previousMetrics.uniqueVisitors,
      icon: FiUsers,
    },
    {
      title: 'New Users',
      value: metrics.newUsers,
      change: calculatePercentageChange(metrics.newUsers, previousMetrics.newUsers),
      isPositive: metrics.newUsers >= previousMetrics.newUsers,
      icon: FiUserPlus,
    },
    {
      title: 'Active Users',
      value: metrics.activeUsers,
      change: calculatePercentageChange(metrics.activeUsers, previousMetrics.activeUsers),
      isPositive: metrics.activeUsers >= previousMetrics.activeUsers,
      icon: FiActivity,
    },
  ];

  const getInsightStyle = color => {
    const styles = {
      green: 'bg-green-50 border-green-100 text-green-800',
      blue: 'bg-blue-50 border-blue-100 text-blue-800',
      orange: 'bg-orange-50 border-orange-100 text-orange-800',
      red: 'bg-red-50 border-red-100 text-red-800',
      purple: 'bg-purple-50 border-purple-100 text-purple-800',
      gray: 'bg-gray-50 border-gray-100 text-gray-700',
    };
    return styles[color] || styles.gray;
  };

  return (
    <div className="p-4 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi min-h-screen">
      <div className="p-6 mx-auto max-w-7xl">
        <div className="flex flex-col mb-8">
          <Title title="Report & Analytics" subtitle="User Activity" />
          <div className="flex items-center mt-3 text-sm text-gray-600">
            <FiCalendar className="w-4 h-4 mr-2" />
            <span>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading analytics data...</div>
          </div>
        ) : (
          <>
            {/* Metric Cards */}
            <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2 xl:grid-cols-4">
              {metricCards.map((card, i) => (
                <MetricCard key={i} {...card} />
              ))}
            </div>

            {/* Chart + Date Picker */}
            <div className="mb-8 overflow-hidden bg-white border border-gray-100 rounded-xl">
              <div className="flex flex-col justify-between px-6 py-4 border-b border-gray-100 md:flex-row md:items-center">
                <div>
                  <h2 className="text-base font-semibold text-gray-800">User Activity Breakdown</h2>
                  <p className="mt-1 text-sm text-gray-500">Selected day overview</p>
                </div>
                <div className="flex items-center mt-4 space-x-2 md:mt-0">
                  <div className="p-2 rounded-lg bg-gray-50">
                    <FiCalendar className="w-4 h-4 text-gray-600" />
                  </div>
                  <select
                    value={selectedMonth}
                    onChange={e => setSelectedMonth(Number(e.target.value))}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400">
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString('en', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedDay}
                    onChange={e => setSelectedDay(Number(e.target.value))}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400">
                    {Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={e => setSelectedYear(Number(e.target.value))}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400">
                    {Array.from({ length: 6 }, (_, i) => {
                      const y = new Date().getFullYear() - i;
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="p-6">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barChartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      barGap={16}
                      barCategoryGap={80}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.6} />
                      <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 13, fill: '#4b5563' }}
                        height={50}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#4b5563' }}
                        tickFormatter={v => v.toLocaleString()}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 min-w-[220px]">
                                <p className="pb-1 mb-2 font-semibold text-gray-800 border-b">
                                  {label}
                                </p>
                                {payload.map((entry, i) => (
                                  <div key={i} className="flex justify-between text-sm py-0.5">
                                    <span style={{ color: entry.color }} className="font-medium">
                                      {entry.name}
                                    </span>
                                    <span className="font-medium">
                                      {entry.value.toLocaleString()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        }}
                        cursor={{ fill: 'rgba(59,130,246,0.08)' }}
                      />
                      <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '12px' }} />

                      <Bar dataKey="New Users" fill="#ef4444" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="Active Users" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="Unique Visitors" fill="#10b981" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="Visits" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {chartData.length === 0 && !loading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                    <p className="text-base text-gray-600">No data available for this date</p>
                  </div>
                )}
              </div>
            </div>

            {/* Insights + Summary */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="p-6 bg-white border border-gray-100 rounded-xl">
                <h3 className="mb-5 text-base font-semibold text-gray-800">Key Insights</h3>
                <div className="space-y-4">
                  {insights.length > 0 ? (
                    insights.map((insight, i) => (
                      <div
                        key={i}
                        className={`p-4 rounded-lg border ${getInsightStyle(insight.color)}`}>
                        <p className="text-sm font-medium">{insight.message}</p>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 border border-gray-100 rounded-lg bg-gray-50">
                      <p className="text-sm text-gray-600">
                        No significant insights yet. More data will unlock better observations.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 bg-white border border-gray-100 rounded-xl">
                <h3 className="mb-5 text-base font-semibold text-gray-800">Today's Summary</h3>
                <div className="space-y-4">
                  {[
                    { label: 'Visits', value: metrics.visitCount, ref: null },
                    {
                      label: 'Unique Visitors',
                      value: metrics.uniqueVisitors,
                      ref: metrics.visitCount,
                      suffix: 'of total visits',
                    },
                    {
                      label: 'Active Users',
                      value: metrics.activeUsers,
                      ref: metrics.uniqueVisitors,
                      suffix: 'of unique visitors',
                    },
                    {
                      label: 'New Users',
                      value: metrics.newUsers,
                      ref: metrics.uniqueVisitors,
                      suffix: 'of unique visitors',
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                      <span className="text-sm text-gray-600">{item.label}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-base font-semibold text-gray-800">
                          {item.value.toLocaleString()}
                        </span>
                        {item.ref > 0 && item.ref !== item.value && (
                          <span className="text-xs text-gray-500">
                            {((item.value / item.ref) * 100).toFixed(1)}% {item.suffix}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  change: PropTypes.number.isRequired,
  isPositive: PropTypes.bool.isRequired,
  icon: PropTypes.elementType.isRequired,
};

export default ReportAnalytics;
